import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Modal, Form, Alert, Spinner, InputGroup, FormControl } from "react-bootstrap";

function CollectionManager() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isPublic: false,
    imageUrl: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  
  // States for adding recipes
  const [showAddRecipesModal, setShowAddRecipesModal] = useState(false);
  const [selectedCollectionForRecipes, setSelectedCollectionForRecipes] = useState(null);
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [recipeSearchQuery, setRecipeSearchQuery] = useState("");
  const [selectedRecipes, setSelectedRecipes] = useState(new Set());
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  
  // States for managing recipes in collection
  const [showManageRecipesModal, setShowManageRecipesModal] = useState(false);
  const [collectionRecipes, setCollectionRecipes] = useState([]);
  const [recipesToRemove, setRecipesToRemove] = useState(new Set());

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/collections`,
        { credentials: "include" }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch collections");
      }
      
      const data = await response.json();
      setCollections(data.collections || []);
    } catch (err) {
      console.error("Error fetching collections:", err);
      setError("Failed to load collections");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCollection = () => {
    setModalMode("create");
    setFormData({ name: "", description: "", isPublic: false, imageUrl: "" });
    setSelectedCollection(null);
    setSelectedFileName("");
    setShowModal(true);
  };

  const handleEditCollection = (collection) => {
    setModalMode("edit");
    setFormData({
      name: collection.name,
      description: collection.description || "",
      isPublic: collection.is_public === 1,
      imageUrl: collection.image_url || ""
    });
    setSelectedCollection(collection);
    setSelectedFileName("");
    setShowModal(true);
  };

  const handleDeleteCollection = async (collection) => {
    if (!window.confirm(`Are you sure you want to delete "${collection.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/collections/${collection.id}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete collection");
      }

      setSuccess("Collection deleted successfully");
      fetchCollections();
    } catch (err) {
      console.error("Error deleting collection:", err);
      setError("Failed to delete collection");
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError("Please select a valid image file");
      return;
    }

    setSelectedFileName(file.name);
    setUploadingImage(true);
    setError("");

    const uploadFormData = new FormData();
    uploadFormData.append("id", modalMode === "edit" ? selectedCollection.id : "temp-" + Date.now());
    uploadFormData.append("image", file);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/upload-collection-image`,
        {
          method: "POST",
          credentials: "include",
          body: uploadFormData
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Upload error:", errorText);
        throw new Error("Failed to upload image");
      }

      const data = await response.json();
      setFormData(prev => ({ ...prev, imageUrl: data.imageUrl }));
      setSuccess("Image uploaded successfully");
    } catch (err) {
      console.error("Error uploading image:", err);
      setError("Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Collection name is required");
      return;
    }

    try {
      const url = modalMode === "create"
        ? `${process.env.REACT_APP_API_URL}/api/collections`
        : `${process.env.REACT_APP_API_URL}/api/collections/${selectedCollection.id}`;
      
      const method = modalMode === "create" ? "POST" : "PUT";

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name.trim(),
          description: formData.description.trim(),
          isPublic: formData.isPublic,
          imageUrl: formData.imageUrl
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${modalMode} collection`);
      }

      setSuccess(`Collection ${modalMode === "create" ? "created" : "updated"} successfully`);
      setShowModal(false);
      fetchCollections();
    } catch (err) {
      console.error(`Error ${modalMode}ing collection:`, err);
      setError(`Failed to ${modalMode} collection`);
    }
  };

  const handleViewCollection = (collection) => {
    window.location.href = `${process.env.REACT_APP_CLIENT_URL}/collection/${collection.id}`;
  };
  
  const handleManageRecipes = async (collection) => {
    console.log("Opening manage recipes modal for collection:", collection);
    setSelectedCollectionForRecipes(collection);
    setShowManageRecipesModal(true);
    setRecipesToRemove(new Set());
    setError("");
    setSuccess("");
    
    // Fetch recipes in this collection
    setLoadingRecipes(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/collections/${collection.id}`;
      console.log("Fetching collection recipes from:", url);
      
      const response = await fetch(url, { credentials: "include" });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API Error Response:", response.status, errorText);
        throw new Error(`Failed to fetch collection recipes: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("Collection API response data:", data);
      
      // The API returns { collection: { ...collectionData, recipes: [...] } }
      const recipes = data.collection?.recipes || [];
      console.log("Recipes in collection:", recipes);
      
      setCollectionRecipes(recipes);
    } catch (err) {
      console.error("Error fetching collection recipes:", err);
      setError(`Failed to load collection recipes: ${err.message}`);
      setCollectionRecipes([]); // Ensure we have an empty array on error
    } finally {
      setLoadingRecipes(false);
    }
  };
  
  const handleToggleRecipeRemoval = (recipeId) => {
    const newSelection = new Set(recipesToRemove);
    if (newSelection.has(recipeId)) {
      newSelection.delete(recipeId);
    } else {
      newSelection.add(recipeId);
    }
    setRecipesToRemove(newSelection);
  };
  
  const handleRemoveRecipesFromCollection = async () => {
    if (recipesToRemove.size === 0) {
      setError("Please select at least one recipe to remove");
      return;
    }
    
    setError("");
    setLoadingRecipes(true);
    let successCount = 0;
    let failedCount = 0;
    
    for (const recipeId of recipesToRemove) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/collections/${selectedCollectionForRecipes.id}/recipes/${recipeId}`,
          {
            method: "DELETE",
            credentials: "include"
          }
        );
        
        if (response.ok) {
          successCount++;
        } else {
          failedCount++;
        }
      } catch (err) {
        console.error("Error removing recipe from collection:", err);
        failedCount++;
      }
    }
    
    setLoadingRecipes(false);
    
    if (successCount > 0) {
      setSuccess(`Successfully removed ${successCount} recipe${successCount > 1 ? 's' : ''} from collection`);
      setShowManageRecipesModal(false);
      fetchCollections(); // Refresh to update recipe counts
    }
    
    if (failedCount > 0) {
      setError(`Failed to remove ${failedCount} recipe${failedCount > 1 ? 's' : ''}`);
    }
  };

  const handleOpenAddRecipes = async (collection) => {
    setSelectedCollectionForRecipes(collection);
    setShowAddRecipesModal(true);
    setSelectedRecipes(new Set());
    setRecipeSearchQuery("");
    setError(""); // Clear any previous errors
    setSuccess(""); // Clear any previous success messages
    
    // Fetch available recipes
    setLoadingRecipes(true);
    try {
      // First get recipes already in collection
      const collectionResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/collections/${collection.id}`,
        { credentials: "include" }
      );
      
      if (!collectionResponse.ok) {
        throw new Error("Failed to fetch collection details");
      }
      
      const collectionData = await collectionResponse.json();
      console.log("Collection data:", collectionData); // Debug log
      
      // The API returns { collection: { ...collectionData, recipes: [...] } }
      const collectionRecipes = collectionData.collection?.recipes || [];
      
      // Handle both possible ID field names (id or recipe_id)
      const recipesInCollection = new Set(
        collectionRecipes.map(r => r.id || r.recipe_id)
      );
      
      // Then get all recipes
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipes`,
        { credentials: "include" }
      );
      
      if (!response.ok) {
        throw new Error("Failed to fetch recipes");
      }
      
      const data = await response.json();
      console.log("Recipes API response:", data); // Debug log
      
      // Handle different response structures
      const allRecipes = Array.isArray(data) ? data : (data.recipes || []);
      
      // Filter out recipes already in collection
      const recipesNotInCollection = allRecipes.filter(recipe => {
        const recipeId = recipe.id || recipe.recipe_id;
        return !recipesInCollection.has(recipeId);
      });
      setAvailableRecipes(recipesNotInCollection);
    } catch (err) {
      console.error("Error fetching recipes:", err);
      setError("Failed to load recipes");
    } finally {
      setLoadingRecipes(false);
    }
  };

  const handleToggleRecipeSelection = (recipeId) => {
    const newSelection = new Set(selectedRecipes);
    if (newSelection.has(recipeId)) {
      newSelection.delete(recipeId);
    } else {
      newSelection.add(recipeId);
    }
    setSelectedRecipes(newSelection);
  };

  const handleAddRecipesToCollection = async () => {
    if (selectedRecipes.size === 0) {
      setError("Please select at least one recipe to add");
      return;
    }

    setError(""); // Clear any previous errors
    setLoadingRecipes(true);
    let successCount = 0;
    let failedCount = 0;

    for (const recipeId of selectedRecipes) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/collections/${selectedCollectionForRecipes.id}/recipes`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ recipeId })
          }
        );

        if (response.ok) {
          successCount++;
        } else {
          failedCount++;
        }
      } catch (err) {
        console.error("Error adding recipe to collection:", err);
        failedCount++;
      }
    }

    setLoadingRecipes(false);
    
    if (successCount > 0) {
      setSuccess(`Successfully added ${successCount} recipe${successCount > 1 ? 's' : ''} to collection`);
      setShowAddRecipesModal(false);
      fetchCollections(); // Refresh to update recipe counts
    }
    
    if (failedCount > 0) {
      setError(`Failed to add ${failedCount} recipe${failedCount > 1 ? 's' : ''}`);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading collections...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>My Collections</h2>
          <p className="text-muted">Organize your recipes into collections for easy browsing.</p>
        </Col>
        <Col xs="auto">
          <Button 
            variant="primary" 
            onClick={handleCreateCollection}
            style={{ 
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            <i className="bi bi-plus-lg me-2"></i>
            Create New Collection
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}

      {collections.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <h4>No collections yet</h4>
            <p className="text-muted">Create your first collection to start organizing your recipes!</p>
            <Button 
              variant="primary" 
              onClick={handleCreateCollection}
              style={{ 
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
            >
              <i className="bi bi-plus-lg me-2"></i>
              Create Your First Collection
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Row>
          {collections.map(collection => (
            <Col key={collection.id} sm={6} md={4} lg={3} className="mb-4">
              <Card 
                className="h-100 shadow-sm"
                style={{ 
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
                }}
              >
                {collection.image_url && (
                  <Card.Img 
                    variant="top" 
                    src={collection.image_url} 
                    style={{ height: "150px", objectFit: "cover" }}
                  />
                )}
                <Card.Body className="p-3">
                  <Card.Title className="mb-2" style={{ fontSize: "1.1rem" }}>{collection.name}</Card.Title>
                  <Card.Text className="mb-2" style={{ fontSize: "0.9rem" }}>
                    {collection.description ? 
                      (collection.description.length > 50 ? 
                        collection.description.substring(0, 50) + "..." : 
                        collection.description) : 
                      <span className="text-muted">No description</span>
                    }
                  </Card.Text>
                  <div className="mb-2">
                    <small className="text-muted">
                      {collection.actual_recipe_count || 0} recipes
                      {collection.is_public === 1 && " • Public"}
                    </small>
                  </div>
                  <div className="d-flex flex-column gap-1">
                    <Button 
                      variant="primary" 
                      className="w-100"
                      size="sm"
                      onClick={() => handleViewCollection(collection)}
                      style={{ 
                        borderRadius: '6px',
                        padding: '6px',
                        fontWeight: '500',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <i className="bi bi-eye me-1" style={{ fontSize: '0.9rem' }}></i>
                      View Recipes
                    </Button>
                    
                    <div className="row g-1">
                      <div className="col-6">
                        <Button 
                          variant="success" 
                          className="w-100"
                          size="sm"
                          onClick={() => handleOpenAddRecipes(collection)}
                          style={{ 
                            borderRadius: '6px',
                            padding: '5px',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <i className="bi bi-plus-circle me-1"></i>
                          Add
                        </Button>
                      </div>
                      <div className="col-6">
                        <Button 
                          variant="warning" 
                          className="w-100"
                          size="sm"
                          onClick={() => handleManageRecipes(collection)}
                          style={{ 
                            borderRadius: '6px',
                            padding: '5px',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            color: 'white'
                          }}
                        >
                          <i className="bi bi-dash-circle me-1"></i>
                          Remove
                        </Button>
                      </div>
                    </div>
                    
                    <div className="row g-1">
                      <div className="col-6">
                        <Button 
                          variant="outline-secondary" 
                          className="w-100"
                          size="sm"
                          onClick={() => handleEditCollection(collection)}
                          style={{ 
                            borderRadius: '6px',
                            padding: '5px',
                            fontSize: '0.875rem',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </Button>
                      </div>
                      <div className="col-6">
                        <Button 
                          variant="outline-danger" 
                          className="w-100"
                          size="sm"
                          onClick={() => handleDeleteCollection(collection)}
                          style={{ 
                            borderRadius: '6px',
                            padding: '5px',
                            fontSize: '0.875rem',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "create" ? "Create New Collection" : "Edit Collection"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter collection name"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter collection description (optional)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Make this collection public"
                checked={formData.isPublic}
                onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
              />
              <Form.Text className="text-muted">
                Public collections can be viewed by other users
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Collection Image</Form.Label>
              {formData.imageUrl && (
                <div className="mb-3">
                  <img 
                    src={formData.imageUrl} 
                    alt="Collection" 
                    style={{ maxWidth: "100%", maxHeight: "200px", objectFit: "cover", borderRadius: "8px" }}
                  />
                </div>
              )}
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ display: 'none' }}
                  id="collection-image-upload"
                />
                <label htmlFor="collection-image-upload">
                  <Button
                    as="span"
                    variant="primary"
                    disabled={uploadingImage}
                    style={{ cursor: uploadingImage ? 'not-allowed' : 'pointer' }}
                  >
                    <i className="bi bi-upload me-2"></i>
                    Choose Image
                  </Button>
                </label>
                {selectedFileName && !uploadingImage && (
                  <span className="ms-2 text-muted">
                    Selected: {selectedFileName}
                  </span>
                )}
                {formData.imageUrl && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, imageUrl: "" }));
                      setSelectedFileName("");
                    }}
                    disabled={uploadingImage}
                  >
                    Remove Image
                  </Button>
                )}
              </div>
              {uploadingImage && (
                <div className="mt-2">
                  <Spinner animation="border" size="sm" /> Uploading {selectedFileName}...
                </div>
              )}
              <Form.Text className="text-muted d-block mt-2">
                Upload an image for your collection (JPG, PNG, GIF supported)
                <br />
                <small>Recommended: At least 400x200 pixels (images display at 200px height)</small>
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {modalMode === "create" ? "Create" : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add Recipes Modal */}
      <Modal 
        show={showAddRecipesModal} 
        onHide={() => {
          setShowAddRecipesModal(false);
          setError(""); // Clear any errors when closing
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Add Recipes to "{selectedCollectionForRecipes?.name}"
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
          
          <InputGroup className="mb-3">
            <FormControl
              placeholder="Search recipes..."
              value={recipeSearchQuery}
              onChange={(e) => setRecipeSearchQuery(e.target.value)}
            />
          </InputGroup>

          {loadingRecipes ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading recipes...</p>
            </div>
          ) : (
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {availableRecipes.length === 0 ? (
                <p className="text-center text-muted py-4">
                  No recipes available to add
                </p>
              ) : (
                <Form>
                  {(() => {
                    const filteredRecipes = availableRecipes.filter(recipe => {
                      const recipeName = recipe.recipe_name || recipe.name || '';
                      return recipeName.toLowerCase().includes(recipeSearchQuery.toLowerCase());
                    });
                    
                    if (filteredRecipes.length === 0) {
                      return (
                        <p className="text-center text-muted py-4">
                          No recipes match your search
                        </p>
                      );
                    }
                    
                    return filteredRecipes.map(recipe => {
                      const recipeId = recipe.id || recipe.recipe_id;
                      const recipeName = recipe.recipe_name || recipe.name || 'Unnamed Recipe';
                      
                      return (
                        <Form.Check
                          key={recipeId}
                          type="checkbox"
                          id={`recipe-${recipeId}`}
                          label={
                            <div>
                              <strong>{recipeName}</strong>
                              {recipe.description && (
                                <small className="text-muted d-block">
                                  {recipe.description.substring(0, 100)}
                                  {recipe.description.length > 100 && "..."}
                                </small>
                              )}
                            </div>
                          }
                          checked={selectedRecipes.has(recipeId)}
                          onChange={() => handleToggleRecipeSelection(recipeId)}
                          className="mb-3"
                        />
                      );
                    });
                  })()}
                </Form>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <span className="text-muted">
              {selectedRecipes.size} recipe{selectedRecipes.size !== 1 ? 's' : ''} selected
            </span>
            <div>
              <Button 
                variant="secondary" 
                onClick={() => setShowAddRecipesModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={handleAddRecipesToCollection}
                disabled={selectedRecipes.size === 0 || loadingRecipes}
              >
                Add Selected
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Manage (Remove) Recipes Modal */}
      <Modal 
        show={showManageRecipesModal} 
        onHide={() => {
          setShowManageRecipesModal(false);
          setError("");
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Recipes in "{selectedCollectionForRecipes?.name}"
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}
          
          <p className="text-muted mb-3">
            Select recipes to remove from this collection:
          </p>

          {loadingRecipes ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading recipes...</p>
            </div>
          ) : (
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {collectionRecipes.length === 0 ? (
                <p className="text-center text-muted py-4">
                  No recipes in this collection
                </p>
              ) : (
                <Form>
                  {collectionRecipes.map(recipe => {
                    const recipeId = recipe.id || recipe.recipe_id;
                    const recipeName = recipe.name || recipe.recipe_name || 'Unnamed Recipe';
                    
                    return (
                      <Form.Check
                        key={recipeId}
                        type="checkbox"
                        id={`remove-recipe-${recipeId}`}
                        label={
                          <div>
                            <strong>{recipeName}</strong>
                            {recipe.course && (
                              <small className="text-muted d-block">
                                Course: {recipe.course}
                              </small>
                            )}
                          </div>
                        }
                        checked={recipesToRemove.has(recipeId)}
                        onChange={() => handleToggleRecipeRemoval(recipeId)}
                        className="mb-3"
                      />
                    );
                  })}
                </Form>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <span className="text-muted">
              {recipesToRemove.size} recipe{recipesToRemove.size !== 1 ? 's' : ''} selected for removal
            </span>
            <div>
              <Button 
                variant="secondary" 
                onClick={() => setShowManageRecipesModal(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button 
                variant="warning" 
                onClick={handleRemoveRecipesFromCollection}
                disabled={recipesToRemove.size === 0 || loadingRecipes}
              >
                Remove Selected
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CollectionManager;